/**
 * Cited phenomena catalog — the "why did that happen?" reference behind every
 * event the game logs. Each entry ties a root-cause structural / aeroelastic /
 * geotechnical phenomenon to a short player-facing blurb and a REAL citation
 * link (Wikipedia where one exists, otherwise the governing standard).
 *
 * The EventLog UI (src/game/eventLog.ts) looks entries up by `id` to render an
 * expandable explanation + "Learn more" link. This is deliberately a plain data
 * module with no dependencies so both the sim and future advanced-physics code
 * can reference the same catalog.
 *
 * See docs/ENGINEERING.md for the engineering grounding and docs/PHENOMENA.md
 * for the table + maintainer hook points.
 */

export interface Phenomenon {
  id: string;
  name: string;
  category: "structural" | "aeroelastic" | "geotechnical" | "loading" | "material";
  /** One-line summary shown in tight UI (timeline row / tooltip). */
  short: string;
  /** A couple of sentences a curious player learns from. */
  explanation: string;
  /** Real link — Wikipedia article or the governing standard. */
  moreInfoUrl: string;
}

/**
 * The catalog. Keys are the stable `phenomenonId` the maintainer passes to
 * `EventLog.log()`. Do not rename keys — future code and saved logs key off them.
 */
export const PHENOMENA: Record<string, Phenomenon> = {
  "euler-buckling": {
    id: "euler-buckling",
    name: "Euler buckling",
    category: "structural",
    short: "Slender member snapped sideways under compression.",
    explanation:
      "A slender column carrying compression can suddenly bow out sideways long before the steel would crush — this elastic instability is called buckling. The critical load Pcr = π²EI/(KL)² falls with the square of the unsupported length, so long, thin legs and bracing are the first to go; guyed masts fight this by cutting the buckling length to the segment between guy levels.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Buckling",
  },
  yielding: {
    id: "yielding",
    name: "Yielding (plastic)",
    category: "material",
    short: "Steel stressed past its yield strength and permanently deformed.",
    explanation:
      "When the stress in a member exceeds the steel's yield strength (Fy ≈ 250 MPa for A36, 345 MPa for A572 Gr50) it stops springing back and deforms permanently. Stocky members yield rather than buckle; the tension-yield capacity is simply Pt = A·Fy, the cross-section area times the yield stress.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Yield_(engineering)",
  },
  "tension-fracture": {
    id: "tension-fracture",
    name: "Tension fracture",
    category: "material",
    short: "A member or cable pulled apart under tension.",
    explanation:
      "Under enough pure tension a member or guy strand reaches its ultimate strength and physically parts. In towers this is most common in windward legs and guy wires, where the overturning couple concentrates as tension — and a snapped guy instantly dumps its load onto its neighbours.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Fracture",
  },
  "p-delta": {
    id: "p-delta",
    name: "P-delta (second-order effect)",
    category: "structural",
    short: "Sway adds extra bending that amplifies itself.",
    explanation:
      "Once a tower leans, its own weight now acts at a horizontal offset (P times delta), adding bending moment that pushes it to lean further — a self-amplifying second-order effect. Slender, flexible structures must be checked with these geometry-updated (second-order) loads or they can be dangerously under-designed.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/P-delta_effect",
  },
  resonance: {
    id: "resonance",
    name: "Resonance (natural frequency)",
    category: "aeroelastic",
    short: "Excitation near the tower's natural frequency amplifies motion.",
    explanation:
      "Every structure has natural frequencies at which it prefers to sway. When a periodic force — gusts, machinery, or shedding vortices — arrives near one of those frequencies, each push adds to the last and the amplitude grows far beyond the static deflection, sometimes to failure. Damping and detuning the structure are the defences.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Resonance",
  },
  "vortex-induced-vibration": {
    id: "vortex-induced-vibration",
    name: "Vortex-induced vibration",
    category: "aeroelastic",
    short: "Alternating wind vortices shake a member crosswind.",
    explanation:
      "Wind peeling off a round member sheds vortices alternately from each side, creating a fluctuating crosswind force at the Strouhal frequency. When that frequency locks onto a natural frequency of a leg, guy, or antenna pole, it can build large steady crosswind oscillations — the classic culprit behind humming, galloping-prone masts and fatigue cracks.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Vortex-induced_vibration",
  },
  galloping: {
    id: "galloping",
    name: "Galloping",
    category: "aeroelastic",
    short: "Ice-shaped cables swing in large, low-frequency arcs.",
    explanation:
      "When ice builds an asymmetric aerofoil shape on a guy or conductor, the wind can feed energy into its motion so a small nudge grows into large, low-frequency vertical swings (Den Hartog instability). Galloping ice-coated guys is a recognised trigger of tower and line failures because the huge amplitudes overload anchors and members.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Aeroelasticity#Galloping",
  },
  "aeroelastic-flutter": {
    id: "aeroelastic-flutter",
    name: "Aeroelastic flutter",
    category: "aeroelastic",
    short: "Coupled bending-torsion motion draws energy from the wind.",
    explanation:
      "Flutter is a self-feeding instability where two modes of motion (typically bending and twisting) couple through the airflow and extract energy from the wind, so oscillations grow without bound above a critical speed. It is the mechanism behind the 1940 Tacoma Narrows Bridge collapse and a concern for very flexible slender structures.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Aeroelasticity#Flutter",
  },
  "guy-slack": {
    id: "guy-slack",
    name: "Guy going slack",
    category: "structural",
    short: "A guy lost tension, leaving the mast unbraced there.",
    explanation:
      "Guys only work in tension. If pretension is too low or the mast sways toward an anchor, the leeward guy goes slack and stops bracing the mast at that level — which lengthens the unsupported buckling length and can let the mast bow out. Correct pretension (≈8–15% of breaking strength) keeps all guys taut through the design wind.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Guy-wire",
  },
  "guy-cascade": {
    id: "guy-cascade",
    name: "Guy failure cascade",
    category: "structural",
    short: "One broken guy overloads the rest, unzipping the mast.",
    explanation:
      "Guyed masts are highly redundant right up until they are not: when one guy fails (corrosion, ice, fatigue, or anchor pullout) its share of the load transfers to the remaining guys and the mast, which can overload them in turn. This progressive collapse — a cascade — is a leading cause of total guyed-mast loss.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Progressive_collapse",
  },
  overturning: {
    id: "overturning",
    name: "Overturning",
    category: "geotechnical",
    short: "Wind moment beat the tower's resisting moment; it toppled.",
    explanation:
      "A tower stands only while its resisting moment (weight times base half-width plus foundation capacity) exceeds the overturning moment from wind acting up its height. When wind — especially on an iced, antenna-laden structure — pushes that ratio below the code safety factor of about 1.5, the tower rotates about its base and falls.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Overturning_moment",
  },
  sliding: {
    id: "sliding",
    name: "Base sliding",
    category: "geotechnical",
    short: "Horizontal wind force overcame base friction.",
    explanation:
      "Before it overturns, a foundation can simply slide when the horizontal wind shear exceeds the friction and passive resistance between the footing and the soil. Codes require a sliding safety factor as well as an overturning one; heavier footings and keyed or embedded bases resist it.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Shallow_foundation",
  },
  "bearing-failure": {
    id: "bearing-failure",
    name: "Bearing capacity failure",
    category: "geotechnical",
    short: "Soil under the footing was crushed by compression.",
    explanation:
      "The leeward footing of a leaning tower carries a large downward compression. If that pressure exceeds the soil's bearing capacity, the ground shears and the footing punches in, dropping that corner and pitching the tower over. Bearing capacity depends on soil type, footing size, and depth.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Bearing_capacity",
  },
  "uplift-pullout": {
    id: "uplift-pullout",
    name: "Foundation uplift / pullout",
    category: "geotechnical",
    short: "Windward footing or anchor was pulled out of the ground.",
    explanation:
      "The windward side of a tower or a guy anchor is loaded in tension (uplift). If the weight of the footing plus the soil cone it engages is less than that pull, the foundation lifts out of the ground. Guy anchors and lattice leg footings are sized specifically to resist this uplift.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Deep_foundation",
  },
  "anchor-breakout": {
    id: "anchor-breakout",
    name: "Concrete anchor breakout",
    category: "geotechnical",
    short: "A cone of concrete tore out around a tension anchor.",
    explanation:
      "A cast-in or post-installed anchor loaded in tension can rip out a cone-shaped chunk of concrete rather than the steel bolt failing — concrete breakout. Its capacity is governed by concrete strength and embedment depth (the CCD method in ACI 318 Chapter 17), and it often controls the design of base plates and guy-anchor connections.",
    moreInfoUrl: "https://www.concrete.org/publications/aci318buildingcode.aspx",
  },
  "ice-load": {
    id: "ice-load",
    name: "Ice load",
    category: "loading",
    short: "Accreted ice added weight and wind area to every member.",
    explanation:
      "Ice accreting on members, guys, and antennas adds dead weight and, by thickening every element, increases the area the wind pushes on. Ice alone accounts for roughly 37% of tower collapses in field studies; asymmetric or shedding ice also loads the structure unevenly.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Atmospheric_icing",
  },
  "wind-on-ice": {
    id: "wind-on-ice",
    name: "Combined wind-on-ice",
    category: "loading",
    short: "Wind on an already ice-laden tower — the top killer.",
    explanation:
      "The single largest cause of tower collapse (~56% in a US field study) is wind acting on a structure already loaded with ice: the ice adds weight and enlarges every wind-catching surface, so a wind that the bare tower would survive becomes fatal. This combined case is why cold-climate towers are designed for ice-plus-wind, not either alone.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Atmospheric_icing",
  },
  fatigue: {
    id: "fatigue",
    name: "Fatigue",
    category: "material",
    short: "Repeated stress cycles cracked the steel over time.",
    explanation:
      "Members and connections that ride out millions of small wind- and vibration-driven stress cycles can develop and grow cracks well below the static strength — metal fatigue. Welded joints and bolt holes are hotspots; vortex-induced vibration and galloping accelerate it, which is why cumulative cycles, not just peak load, matter.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Fatigue_(material)",
  },
  "gust-amplification": {
    id: "gust-amplification",
    name: "Gust amplification",
    category: "loading",
    short: "A short gust hit harder than the steady wind.",
    explanation:
      "Real wind is turbulent, so short-duration gusts briefly exceed the mean wind speed and — because pressure scales with velocity squared — load the tower well above its steady-state value. Codes fold this into a gust-effect factor (Gh ≈ 0.85 for lattice) applied to the design force; flexible towers can also respond dynamically to gusts near their natural frequency.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Wind_engineering",
  },
  "foundation-settlement": {
    id: "foundation-settlement",
    name: "Foundation settlement",
    category: "geotechnical",
    short: "The ground consolidated and the base sank unevenly.",
    explanation:
      "Over time a foundation can sink as the soil beneath it consolidates; if it settles unevenly (differential settlement) the tower leans, adding P-delta bending and redistributing leg loads. Even without a dramatic collapse, settlement can push a leaning structure past its capacity.",
    moreInfoUrl: "https://en.wikipedia.org/wiki/Settlement_(structural)",
  },
};

/** Stable set of catalog keys (also the valid `phenomenonId` values). */
export type PhenomenonId = keyof typeof PHENOMENA;

/** Look up a phenomenon by id; returns undefined for unknown ids (UI degrades gracefully). */
export function getPhenomenon(id: string): Phenomenon | undefined {
  return PHENOMENA[id];
}
